import React, { useState, useEffect, useRef } from "react";
import { FaFolder, FaSpinner, FaCheck, FaTimes } from "react-icons/fa";
import api from "../api";
import { getApiUrl } from "../utils/apiConfig";
import "../styles/WorkingDirectoryDropdown.css";

const WorkingDirectoryDropdown = ({ selectedProfileUuid }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [currentDirectory, setCurrentDirectory] = useState("");
  const [newDirectory, setNewDirectory] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const dropdownRef = useRef(null);

  const baseURL = `${getApiUrl()}/api`;

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setError("");
        setSuccess("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch current directory when profile changes or dropdown opens
  useEffect(() => {
    if (selectedProfileUuid && showDropdown) {
      fetchCurrentDirectory();
    }
  }, [selectedProfileUuid, showDropdown]);

  const fetchCurrentDirectory = async () => {
    if (!selectedProfileUuid) return;

    setLoading(true);
    setError("");
    
    try {
      const response = await api.get(`${baseURL}/profiles/${selectedProfileUuid}/working-directory/`);
      
      if (response.data.success) {
        setCurrentDirectory(response.data.current_directory || "");
        setNewDirectory(response.data.current_directory || "");
      } else {
        setError(response.data.error || "Failed to get current directory");
      }
    } catch (error) {
      console.error("Error fetching current directory:", error);
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError("Failed to get current directory");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetDirectory = async () => {
    if (!selectedProfileUuid || !newDirectory.trim()) {
      setError("Please enter a directory path");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await api.post(`${baseURL}/profiles/${selectedProfileUuid}/working-directory/`, {
        path: newDirectory.trim()
      });

      if (response.data.success) {
        setCurrentDirectory(response.data.current_directory);
        setNewDirectory(response.data.current_directory);
        setSuccess("Working directory set successfully!");
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError(response.data.error || "Failed to set directory");
      }
    } catch (error) {
      console.error("Error setting directory:", error);
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError("Failed to set directory");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSetDirectory();
    }
  };

  if (!selectedProfileUuid) {
    return null;
  }

  return (
    <div className="working-directory-dropdown" ref={dropdownRef}>
      <button
        className="nav-button"
        onClick={() => {
          setShowDropdown(!showDropdown);
          setError("");
          setSuccess("");
        }}
        title="Set working directory"
      >
        <FaFolder /> Set Working Directory
      </button>

      {showDropdown && (
        <div className="working-directory-dropdown-content">
          <div className="working-directory-header">
            <span>Working Directory</span>
          </div>

          <div className="working-directory-body">
            {loading && (
              <div className="loading-indicator">
                <FaSpinner className="spinner" />
                <span>Loading...</span>
              </div>
            )}

            {!loading && (
              <>
                <div className="current-directory">
                  <label>Current Directory:</label>
                  <div className="directory-display">
                    {currentDirectory || "Not set"}
                  </div>
                </div>

                <div className="new-directory">
                  <label>New Directory Path:</label>
                  <input
                    type="text"
                    value={newDirectory}
                    onChange={(e) => setNewDirectory(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter directory path..."
                    className="directory-input"
                  />
                  <button
                    onClick={handleSetDirectory}
                    disabled={!newDirectory.trim() || loading}
                    className="set-directory-btn"
                  >
                    <FaCheck /> Set Directory
                  </button>
                </div>
              </>
            )}

            {error && (
              <div className="error-message">
                <FaTimes /> {error}
              </div>
            )}

            {success && (
              <div className="success-message">
                <FaCheck /> {success}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkingDirectoryDropdown; 